import { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { useDeleteLlmCredential, useLlmCredentials } from "../../api/queries";
import type { LlmCredentialSummary } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { useToast } from "../../toast/ToastContext";
import { AddLlmCredentialModal } from "./AddLlmCredentialModal";
import { DeleteLlmCredentialDialog } from "./DeleteLlmCredentialDialog";
import "./llm-credentials.css";

const PROVIDER_LABEL: Record<string, string> = {
  openrouter: "OpenRouter",
  gemini: "Google Gemini",
  openai: "OpenAI",
  deepseek: "DeepSeek",
  ollama: "Ollama (local)",
};

const WRITE_ONLY_NOTE =
  "API keys are write-only — sealed on write and never shown again. Rotate or add a new credential to replace one.";

export function LlmCredentialsPage() {
  const { session } = useAuth();
  const { pushToast } = useToast();
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch } = useLlmCredentials({
    cursor: paginator.cursor,
  });
  const deleteMutation = useDeleteLlmCredential();

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LlmCredentialSummary | null>(null);

  // Mirrors the server's authz matrix exactly: llm_credential:create/
  // update/delete are admin-only (server/internal/authz/model.go) — holding
  // one of these tokens is the ability to spend the tenant's whole LLM
  // budget. This is UX only (S-8) — the server enforces the real decision
  // on every request regardless of what this check says.
  const isAdmin = session?.role === "admin";

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const { id, label } = deleteTarget;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          pushToast({ title: `Removed "${label}".`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't remove "${label}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const addButton = (
    <Button variant="primary" onClick={() => setAddOpen(true)}>
      Add credential
    </Button>
  );

  const columns: DataTableColumn<LlmCredentialSummary>[] = [
    { key: "label", header: "Label", cell: (c) => c.label, className: "data-table__primary" },
    { key: "provider", header: "Provider", cell: (c) => PROVIDER_LABEL[c.provider] ?? c.provider },
    {
      key: "models",
      header: "Models",
      cell: (c) => c.models?.join(", ") || "—",
      className: "data-table__secondary",
    },
    {
      key: "budget",
      header: "Budget",
      cell: (c) => (c.budgetMonthlyUsd != null ? `$${c.budgetMonthlyUsd}/mo` : "—"),
      className: "data-table__secondary",
    },
    { key: "createdBy", header: "Added by", cell: (c) => c.createdBy || "—", className: "data-table__secondary" },
    ...(isAdmin
      ? [
          {
            key: "actions",
            header: <span className="visually-hidden">Actions</span>,
            cell: (c: LlmCredentialSummary) => (
              <Button variant="danger" onClick={() => setDeleteTarget(c)}>
                Remove
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="LLM credentials"
        description="API keys for model providers (OpenRouter, Gemini, OpenAI, DeepSeek, Ollama) used by the agentic workbench."
        actions={isAdmin ? addButton : undefined}
      />

      <p className="llm-credentials-note">{WRITE_ONLY_NOTE}</p>

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading LLM credentials…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load LLM credentials. Check your connection and try again."
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No LLM credentials yet"
            description="Add an API key for an LLM provider to start using the agentic workbench."
            action={isAdmin ? addButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(c) => c.id} />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="credentials"
            />
          </>
        )}
      </QueryStates>

      {isAdmin && <AddLlmCredentialModal open={addOpen} onClose={() => setAddOpen(false)} />}

      {isAdmin && (
        <DeleteLlmCredentialDialog
          credential={deleteTarget}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
